import React from 'react'
import { useForm } from '@inertiajs/react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type CrmCoreTeam from '@/types/serializers/CrmCoreTeam'
import {
  TeamGroupedUserPicker,
  type AssignableTeam,
} from './TeamGroupedUserPicker'

interface CreateCampaignSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  teams: CrmCoreTeam[]
  assignableTeams: AssignableTeam[]
}

interface CampaignFormData {
  campaign: {
    name: string
    description: string
    campaign_type: string
    goal_amount_millimes: string
    start_date: string
    end_date: string
    team_ids: number[]
    user_ids: number[]
  }
}

const CAMPAIGN_TYPES = [
  { value: 'fundraising', label: 'Collecte' },
  { value: 'awareness', label: 'Sensibilisation' },
  { value: 'event', label: 'Evenement' },
  { value: 'emergency', label: 'Urgence' },
]

function defaultFormData(): CampaignFormData {
  return {
    campaign: {
      name: '',
      description: '',
      campaign_type: 'fundraising',
      goal_amount_millimes: '',
      start_date: '',
      end_date: '',
      team_ids: [],
      user_ids: [],
    },
  }
}

export function CreateCampaignSheet({
  open,
  onOpenChange,
  teams,
  assignableTeams,
}: CreateCampaignSheetProps) {
  const form = useForm<CampaignFormData>(defaultFormData())

  function handleClose() {
    form.reset()
    onOpenChange(false)
  }

  function handleTeamToggle(teamId: number) {
    const currentIds = form.data.campaign.team_ids
    const newIds = currentIds.includes(teamId)
      ? currentIds.filter((id) => id !== teamId)
      : [...currentIds, teamId]
    form.setData('campaign', {
      ...form.data.campaign,
      team_ids: newIds,
    })
  }

  function handleUserToggle(userId: number) {
    const currentIds = form.data.campaign.user_ids
    const newIds = currentIds.includes(userId)
      ? currentIds.filter((id) => id !== userId)
      : [...currentIds, userId]
    form.setData('campaign', {
      ...form.data.campaign,
      user_ids: newIds,
    })
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    form.post('/fundraising/campaigns', {
      onSuccess: () => {
        form.reset()
        onOpenChange(false)
      },
    })
  }

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent className="w-[520px] sm:max-w-[520px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Nouvelle campagne</SheetTitle>
          <SheetDescription>
            Creez une nouvelle campagne de collecte.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="space-y-4 p-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="camp_name">Nom *</Label>
            <Input
              id="camp_name"
              value={form.data.campaign.name}
              onChange={(e) =>
                form.setData('campaign', {
                  ...form.data.campaign,
                  name: e.target.value,
                })
              }
              placeholder="Nom de la campagne"
              required
            />
            {form.errors['campaign.name'] && (
              <p className="text-sm text-destructive">
                {form.errors['campaign.name']}
              </p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="camp_desc">Description</Label>
            <Input
              id="camp_desc"
              value={form.data.campaign.description}
              onChange={(e) =>
                form.setData('campaign', {
                  ...form.data.campaign,
                  description: e.target.value,
                })
              }
              placeholder="Description de la campagne"
            />
          </div>

          {/* Type */}
          <div className="space-y-2">
            <Label>Type *</Label>
            <Select
              value={form.data.campaign.campaign_type}
              onValueChange={(val) =>
                form.setData('campaign', {
                  ...form.data.campaign,
                  campaign_type: val,
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Choisir un type" />
              </SelectTrigger>
              <SelectContent>
                {CAMPAIGN_TYPES.map((ct) => (
                  <SelectItem key={ct.value} value={ct.value}>
                    {ct.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Goal amount */}
          <div className="space-y-2">
            <Label htmlFor="camp_goal">Objectif (TND)</Label>
            <Input
              id="camp_goal"
              type="number"
              min="0"
              step="0.001"
              value={form.data.campaign.goal_amount_millimes}
              onChange={(e) =>
                form.setData('campaign', {
                  ...form.data.campaign,
                  goal_amount_millimes: e.target.value,
                })
              }
              placeholder="0.000"
            />
          </div>

          {/* Start date */}
          <div className="space-y-2">
            <Label htmlFor="camp_start">Date de debut</Label>
            <Input
              id="camp_start"
              type="date"
              value={form.data.campaign.start_date}
              onChange={(e) =>
                form.setData('campaign', {
                  ...form.data.campaign,
                  start_date: e.target.value,
                })
              }
            />
          </div>

          {/* End date */}
          <div className="space-y-2">
            <Label htmlFor="camp_end">Date de fin</Label>
            <Input
              id="camp_end"
              type="date"
              value={form.data.campaign.end_date}
              onChange={(e) =>
                form.setData('campaign', {
                  ...form.data.campaign,
                  end_date: e.target.value,
                })
              }
            />
          </div>

          {/* Team multi-select */}
          {teams && teams.length > 0 && (
            <div className="space-y-2">
              <Label>Equipes</Label>
              <div className="space-y-1 rounded-md border p-3 max-h-40 overflow-y-auto">
                {teams.map((team) => (
                  <label
                    key={team.id}
                    className="flex items-center gap-2 text-sm cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={form.data.campaign.team_ids.includes(team.id)}
                      onChange={() => handleTeamToggle(team.id)}
                      className="rounded border-gray-300"
                    />
                    {team.name}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Individual user multi-select, grouped by team */}
          <TeamGroupedUserPicker
            assignableTeams={assignableTeams}
            selectedIds={form.data.campaign.user_ids}
            onToggle={handleUserToggle}
          />

          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={form.processing} className="flex-1">
              {form.processing ? 'Creation...' : 'Creer'}
            </Button>
            <Button type="button" variant="outline" onClick={handleClose}>
              Annuler
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
